import { apiClient } from './apiClient';

class ParticipantsService {
  constructor() {
    // Pfad über apiClient
  }

  // Alle Teilnehmer abrufen
  async getAllParticipants() {
    try {
      const response = await apiClient.get('/participants');
      return {
        success: true,
        data: response,
        total: Array.isArray(response) ? response.length : 0
      };
    } catch (error) {
      console.error('Fehler beim Abrufen der Teilnehmer:', error);
      return {
        success: false,
        error: error.message || 'Fehler beim Abrufen der Teilnehmer',
        data: []
      };
    }
  }

  // Teilnehmer nach ID abrufen
  async getParticipantById(id) {
    try {
      const response = await apiClient.get(`/participants/${id}`);
      return {
        success: true,
        data: response
      };
    } catch (error) {
      console.error('Fehler beim Abrufen des Teilnehmers:', error);
      return {
        success: false,
        error: error.message || 'Teilnehmer nicht gefunden',
        data: null
      };
    }
  }

  // Neuen Teilnehmer hinzufügen
  async addParticipant(participantData) {
    try {
      // Daten für Backend-API vorbereiten
      const apiData = {
        name: participantData.name,
        age: parseInt(participantData.age),
        gender: participantData.gender,
        status: participantData.status || 'Ausstehend',
        nationId: participantData.nationId, // ID statt Name
        categoryIds: participantData.categoryIds || [], // IDs statt Namen
        openFormName: participantData.openFormName || null
      };

      const response = await apiClient.post('/participants', apiData);

      return {
        success: true,
        data: response,
        message: 'Teilnehmer erfolgreich hinzugefügt'
      };
    } catch (error) {
      console.error('Fehler beim Hinzufügen des Teilnehmers:', error);
      return {
        success: false,
        error: error.message || 'Fehler beim Hinzufügen des Teilnehmers',
        data: null
      };
    }
  }

  // Teilnehmer bearbeiten
  async updateParticipant(id, participantData) {
    try {
      // Daten für Backend-API vorbereiten
      const apiData = {
        name: participantData.name,
        age: parseInt(participantData.age),
        gender: participantData.gender,
        status: participantData.status || 'Ausstehend',
        nationId: participantData.nationId, // ID statt Name
        categoryIds: participantData.categoryIds || [], // IDs statt Namen
        openFormName: participantData.openFormName || null
      };

      const response = await apiClient.put(`/participants/${id}`, apiData);

      return {
        success: true,
        data: response,
        message: 'Teilnehmer erfolgreich aktualisiert'
      };
    } catch (error) {
      console.error('Fehler beim Aktualisieren des Teilnehmers:', error);
      return {
        success: false,
        error: error.message || 'Fehler beim Aktualisieren des Teilnehmers',
        data: null
      };
    }
  }

  // Teilnehmer löschen
  async deleteParticipant(id) {
    try {
      const response = await apiClient.delete(`/participants/${id}`);

      return {
        success: true,
        data: response,
        message: 'Teilnehmer erfolgreich gelöscht'
      };
    } catch (error) {
      console.error('Fehler beim Löschen des Teilnehmers:', error);
      return {
        success: false,
        error: error.message || 'Fehler beim Löschen des Teilnehmers',
        data: null
      };
    }
  }

  // Teilnehmer nach Nation abrufen (Frontend-Filterung)
  async getParticipantsByNation(nationCode) {
    try {
      const allParticipants = await this.getAllParticipants();
      if (!allParticipants.success) {
        return allParticipants;
      }

      const participants = allParticipants.data.filter(p => 
        p.nation && p.nation.code === nationCode
      );
      
      return {
        success: true,
        data: participants,
        total: participants.length
      };
    } catch (error) {
      console.error('Fehler beim Filtern nach Nation:', error);
      return {
        success: false,
        error: error.message || 'Fehler beim Filtern nach Nation',
        data: []
      };
    }
  }

  // Teilnehmer nach Altersgruppe abrufen (Frontend-Filterung)
  async getParticipantsByAgeGroup(ageGroup) {
    try {
      const allParticipants = await this.getAllParticipants();
      if (!allParticipants.success) {
        return allParticipants;
      }

      const participants = allParticipants.data.filter(p => 
        p.ageGroup && p.ageGroup.name === ageGroup
      );
      
      return {
        success: true,
        data: participants,
        total: participants.length
      };
    } catch (error) {
      console.error('Fehler beim Filtern nach Altersgruppe:', error);
      return {
        success: false,
        error: error.message || 'Fehler beim Filtern nach Altersgruppe',
        data: []
      };
    }
  }

  // Teilnehmer nach Status abrufen (Frontend-Filterung)
  async getParticipantsByStatus(status) {
    try {
      const allParticipants = await this.getAllParticipants();
      if (!allParticipants.success) {
        return allParticipants;
      }

      const participants = allParticipants.data.filter(p => p.status === status);
      
      return {
        success: true,
        data: participants,
        total: participants.length
      };
    } catch (error) {
      console.error('Fehler beim Filtern nach Status:', error);
      return {
        success: false,
        error: error.message || 'Fehler beim Filtern nach Status',
        data: []
      };
    }
  }

  // Teilnehmer nach Geschlecht abrufen (Frontend-Filterung)
  async getParticipantsByGender(gender) {
    try {
      const allParticipants = await this.getAllParticipants();
      if (!allParticipants.success) {
        return allParticipants;
      }

      const participants = allParticipants.data.filter(p => p.gender === gender);
      
      return {
        success: true,
        data: participants,
        total: participants.length
      };
    } catch (error) {
      console.error('Fehler beim Filtern nach Geschlecht:', error);
      return {
        success: false,
        error: error.message || 'Fehler beim Filtern nach Geschlecht',
        data: []
      };
    }
  }

  // Teilnehmer nach Kategorie abrufen (Frontend-Filterung)
  async getParticipantsByCategory(category) {
    try {
      const allParticipants = await this.getAllParticipants();
      if (!allParticipants.success) {
        return allParticipants;
      }

      const participants = allParticipants.data.filter(p => 
        p.categories && p.categories.some(cat => cat.name === category)
      );
      
      return {
        success: true,
        data: participants,
        total: participants.length
      };
    } catch (error) {
      console.error('Fehler beim Filtern nach Kategorie:', error);
      return {
        success: false,
        error: error.message || 'Fehler beim Filtern nach Kategorie',
        data: []
      };
    }
  }

  // Teilnehmer suchen (Frontend-Filterung)
  async searchParticipants(searchTerm) {
    try {
      const allParticipants = await this.getAllParticipants();
      if (!allParticipants.success) {
        return allParticipants;
      }

      const participants = allParticipants.data.filter(p => 
        p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (p.nation && p.nation.name.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      
      return {
        success: true,
        data: participants,
        total: participants.length
      };
    } catch (error) {
      console.error('Fehler bei der Teilnehmer-Suche:', error);
      return {
        success: false,
        error: error.message || 'Fehler bei der Teilnehmer-Suche',
        data: []
      };
    }
  }

  // Gefilterte Teilnehmer abrufen (Frontend-Filterung)
  async getFilteredParticipants(filters = {}) {
    try {
      const allParticipants = await this.getAllParticipants();
      if (!allParticipants.success) {
        return allParticipants;
      }

      let filteredParticipants = [...allParticipants.data];

      // Suche nach Name
      if (filters.searchTerm) {
        filteredParticipants = filteredParticipants.filter(p => 
          p.name.toLowerCase().includes(filters.searchTerm.toLowerCase())
        );
      }

      // Filter nach Status
      if (filters.status && filters.status !== 'all') {
        filteredParticipants = filteredParticipants.filter(p => 
          p.status.toLowerCase() === filters.status.toLowerCase()
        );
      }

      // Filter nach Altersgruppe
      if (filters.ageGroup && filters.ageGroup !== 'all') {
        filteredParticipants = filteredParticipants.filter(p => 
          p.ageGroup && p.ageGroup.name.toLowerCase() === filters.ageGroup.toLowerCase()
        );
      }

      // Filter nach Nation
      if (filters.nation && filters.nation !== 'all') {
        filteredParticipants = filteredParticipants.filter(p => 
          p.nation && p.nation.code === filters.nation
        );
      }

      // Filter nach Geschlecht
      if (filters.gender && filters.gender !== 'all') {
        filteredParticipants = filteredParticipants.filter(p => 
          p.gender === filters.gender
        );
      }

      return {
        success: true,
        data: filteredParticipants,
        total: filteredParticipants.length
      };
    } catch (error) {
      console.error('Fehler beim Filtern der Teilnehmer:', error);
      return {
        success: false,
        error: error.message || 'Fehler beim Filtern der Teilnehmer',
        data: []
      };
    }
  }

  // Gruppen eines Teilnehmers abrufen
  async getParticipantGroups(participantId) {
    try {
      const response = await apiClient.get(`/participants/${participantId}/groups`);
      return {
        success: true,
        data: response || []
      };
    } catch (error) {
      console.error('Fehler beim Abrufen der Teilnehmer-Gruppen:', error);
      return {
        success: false,
        error: error.message || 'Fehler beim Abrufen der Gruppen',
        data: []
      };
    }
  }

  // Statistiken abrufen (Frontend-Berechnung)
  async getStatistics() {
    try {
      const allParticipants = await this.getAllParticipants();
      if (!allParticipants.success) {
        return allParticipants;
      }

      const participants = allParticipants.data;
      const total = participants.length;
      const confirmed = participants.filter(p => p.status === 'Bestätigt').length;
      const pending = participants.filter(p => p.status === 'Ausstehend').length;
      
      const nations = {};
      participants.forEach(p => {
        if (p.nation) {
          nations[p.nation.name] = (nations[p.nation.name] || 0) + 1;
        }
      });
      
      const ageGroups = {};
      participants.forEach(p => {
        if (p.ageGroup) {
          ageGroups[p.ageGroup.name] = (ageGroups[p.ageGroup.name] || 0) + 1;
        }
      });
      
      return {
        success: true,
        data: {
          total,
          confirmed,
          pending,
          nations,
          ageGroups
        }
      };
    } catch (error) {
      console.error('Fehler beim Abrufen der Statistiken:', error);
      return {
        success: false,
        error: error.message || 'Fehler beim Abrufen der Statistiken',
        data: {
          total: 0,
          confirmed: 0,
          pending: 0,
          nations: {},
          ageGroups: {}
        }
      };
    }
  }
}

// Singleton-Instanz exportieren
const participantsService = new ParticipantsService();
export default participantsService; 